import {
  CustomFieldType,
  type DateCustomFieldType,
} from "@repo/app-types/entities/customField";
import { DateTime } from "luxon";

/**
 * @file
 * Display formatting for the two date-shaped custom field types. Shared so
 * the contact panel, the interview slideover, and the filter chips can't
 * drift into rendering the same stored value three different ways.
 */

/**
 * Display form of an ISO calendar date (`YYYY-MM-DD`) — a stored `date`
 * value, or a day picked in a filter. Read in UTC because a calendar date has
 * no instant behind it: parsing it in the viewer's zone would roll it back a
 * day for anyone at a negative offset.
 */
export function formatCustomFieldCalendarDate(value: string): string {
  const date = DateTime.fromISO(value, { zone: "utc" });
  return date.isValid ? date.toLocaleString(DateTime.DATE_MED) : value;
}

/**
 * Display form of a stored `date` / `date_and_time` custom field value.
 * `date_and_time` values are instants, so they render in the viewer's zone;
 * `date` values are calendar days and render as stored. Anything that doesn't
 * parse falls through unchanged rather than surfacing "Invalid DateTime".
 */
export function formatCustomFieldDateValue(
  fieldType: DateCustomFieldType,
  value: unknown,
): string {
  const rawValue = String(value);

  if (fieldType === CustomFieldType.Date) {
    return formatCustomFieldCalendarDate(rawValue);
  }

  // `fromISO` resolves the value into the viewer's zone by default, so an
  // offset-bearing timestamp lands on the right local wall clock.
  const dateTime = DateTime.fromISO(rawValue);
  return dateTime.isValid
    ? dateTime.toLocaleString(DateTime.DATETIME_MED)
    : rawValue;
}
